/**
 * Lets open overlays publish a dismiss handler so OS/browser back (W7) can
 * close the sheet before hierarchical journey navigation.
 *
 * Registrations form a LIFO stack: `overlayDismiss` is always the top entry.
 * Prefer `registerOverlayDismiss(fn) => unregister`. `setOverlayDismiss` remains
 * a thin adapter: `set(fn)` pushes; `set(null)` pops that publisher's entry
 * (LIFO among set() callers — matches React effect cleanup order).
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

type StackEntry = {
  readonly id: number;
  readonly dismiss: () => void;
};

export interface OverlayBackDismissContextValue {
  /** Top-of-stack overlay dismiss; null when the stack is empty. */
  readonly overlayDismiss: (() => void) | null;
  /**
   * Push a dismiss handler. Returns unregister that removes this entry by id
   * (restores the previous top when the removed entry was top).
   */
  readonly registerOverlayDismiss: (dismiss: () => void) => () => void;
  /**
   * Backward-compat adapter: `fn` pushes; `null` pops this adapter's last push
   * (nested set/null pairs work when cleanup order is LIFO). Prefer register.
   */
  readonly setOverlayDismiss: (dismiss: (() => void) | null) => void;
}

const OverlayBackDismissContext = createContext<OverlayBackDismissContextValue | null>(null);

export function OverlayBackDismissProvider({
  children,
}: {
  readonly children: ReactNode;
}): JSX.Element {
  const stackRef = useRef<StackEntry[]>([]);
  const nextIdRef = useRef(0);
  /** Ids pushed via setOverlayDismiss, LIFO for matching set(null). */
  const setApiIdsRef = useRef<number[]>([]);
  const [overlayDismiss, setOverlayDismissState] = useState<(() => void) | null>(null);

  const syncTop = useCallback((): void => {
    const top = stackRef.current[stackRef.current.length - 1];
    setOverlayDismissState(() => (top !== undefined ? top.dismiss : null));
  }, []);

  const removeById = useCallback(
    (id: number): void => {
      const before = stackRef.current;
      const next = before.filter((entry) => entry.id !== id);
      if (next.length === before.length) {
        return;
      }
      stackRef.current = next;
      setApiIdsRef.current = setApiIdsRef.current.filter((entryId) => entryId !== id);
      syncTop();
    },
    [syncTop],
  );

  const registerOverlayDismiss = useCallback(
    (dismiss: () => void): (() => void) => {
      const id = nextIdRef.current + 1;
      nextIdRef.current = id;
      stackRef.current = [...stackRef.current, { id, dismiss }];
      syncTop();
      return (): void => {
        removeById(id);
      };
    },
    [removeById, syncTop],
  );

  const setOverlayDismiss = useCallback(
    (dismiss: (() => void) | null): void => {
      if (dismiss === null) {
        const id = setApiIdsRef.current.pop();
        if (id !== undefined) {
          removeById(id);
        }
        return;
      }
      const id = nextIdRef.current + 1;
      nextIdRef.current = id;
      setApiIdsRef.current.push(id);
      stackRef.current = [...stackRef.current, { id, dismiss }];
      syncTop();
    },
    [removeById, syncTop],
  );

  const value = useMemo(
    (): OverlayBackDismissContextValue => ({
      overlayDismiss,
      registerOverlayDismiss,
      setOverlayDismiss,
    }),
    [overlayDismiss, registerOverlayDismiss, setOverlayDismiss],
  );

  return (
    <OverlayBackDismissContext.Provider value={value}>
      {children}
    </OverlayBackDismissContext.Provider>
  );
}

export function useOverlayBackDismiss(): OverlayBackDismissContextValue {
  const ctx = useContext(OverlayBackDismissContext);
  if (ctx === null) {
    throw new Error('useOverlayBackDismiss must be used within OverlayBackDismissProvider');
  }
  return ctx;
}

/** Safe when overlay mounts outside the shell provider (unit tests). */
export function useOptionalOverlayBackDismiss(): OverlayBackDismissContextValue | null {
  return useContext(OverlayBackDismissContext);
}
